import { useEffect, useRef, useState } from 'react';
import type { BrandAsset } from '../../services/editingApi';
import type { EditList } from '../../utils/editing/editList';
import { outputDurationMs } from '../../utils/editing/editList';

/**
 * Hears the mix while previewing: music under the video (looped from its start
 * point, at its gain, lowered while a voiceover plays) and voiceovers at their
 * times, through plain <audio> elements kept in step with the preview's output
 * clock. The original sound's gain is applied to the <video> itself, and a muted
 * stretch silences it. An approximation of the render's ducking, close enough to
 * judge levels; the export is the truth.
 *
 * Levels: when the elements can be read (their files allow the app's origin, as
 * PithPlay's asset links do), each is routed through an analyser and the hook
 * reports peak levels (0–1) for the Sound panel's meters.
 */

const dbToGain = (db: number): number => Math.min(1, Math.pow(10, db / 20));
/** Re-sync an element when it drifts more than this from the clock. */
const DRIFT_MS = 250;

export interface MixLevels {
  original: number;
  music: number;
  voice: number;
}

const SILENT: MixLevels = { original: 0, music: 0, voice: 0 };

interface Options {
  list: EditList;
  assets: BrandAsset[];
  outputMs: number;
  playing: boolean;
  video: HTMLVideoElement | null;
  muted: boolean;
  /** The stretch playing now has its original sound muted. */
  segmentMuted?: boolean;
  /** Report levels (only while the Sound panel is open). */
  meter?: boolean;
}

type Analysed = { source: MediaElementAudioSourceNode; analyser: AnalyserNode };

export function usePreviewAudio({ list, assets, outputMs, playing, video, muted, segmentMuted = false, meter = false }: Options): MixLevels {
  const musicRef = useRef<HTMLAudioElement | null>(null);
  const musicUrlRef = useRef<string | null>(null);
  const voicesRef = useRef<Record<string, HTMLAudioElement>>({});
  const contextRef = useRef<AudioContext | null>(null);
  const analysedRef = useRef<Map<HTMLMediaElement, Analysed>>(new Map());
  const [levels, setLevels] = useState<MixLevels>(SILENT);

  // One element per sound, created when the list first names it.
  useEffect(() => {
    const musicAsset = list.music ? assets.find((asset) => asset.id === list.music?.assetId) : null;
    const url = musicAsset?.url || null;
    if (url !== musicUrlRef.current) {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current.src = '';
      }
      musicRef.current = url ? makeAudio(url) : null;
      musicUrlRef.current = url;
    }
    const wanted: Record<string, string> = {};
    list.voiceovers.forEach((voiceover) => {
      const asset = voiceover.source.assetId ? assets.find((candidate) => candidate.id === voiceover.source.assetId) : null;
      if (asset) wanted[voiceover.id] = asset.url;
    });
    const current = voicesRef.current;
    Object.keys(current).forEach((id) => {
      if (wanted[id] !== current[id].getAttribute('data-src')) {
        current[id].pause();
        delete current[id];
      }
    });
    Object.keys(wanted).forEach((id) => {
      if (!current[id]) {
        const el = makeAudio(wanted[id]);
        el.setAttribute('data-src', wanted[id]);
        current[id] = el;
      }
    });
  }, [assets, list.music, list.voiceovers]);

  // Keep everything in step with the clock.
  useEffect(() => {
    const total = outputDurationMs(list);
    let voiceSpeaking = false;
    list.voiceovers.forEach((voiceover) => {
      const el = voicesRef.current[voiceover.id];
      if (!el) return;
      const local = outputMs - voiceover.atMs;
      const lengthMs = Number.isFinite(el.duration) ? el.duration * 1000 : Infinity;
      const active = playing && !muted && local >= 0 && local < lengthMs;
      el.volume = dbToGain(voiceover.gainDb);
      if (active) {
        voiceSpeaking = voiceSpeaking || voiceover.duckOriginal;
        if (Math.abs(el.currentTime * 1000 - local) > DRIFT_MS) el.currentTime = local / 1000;
        if (el.paused) el.play().catch(() => undefined);
      } else if (!el.paused) {
        el.pause();
      }
    });
    if (video) {
      const base = segmentMuted ? 0 : dbToGain(list.audio.originalGainDb);
      video.volume = Math.max(0, Math.min(1, voiceSpeaking ? base * 0.3 : base));
    }
    const music = musicRef.current;
    if (music && list.music) {
      const local = outputMs - list.music.startMs;
      const active = playing && !muted && local >= 0 && outputMs < total;
      music.loop = false;
      const ducked = list.music.ducking && voiceSpeaking ? dbToGain(list.music.gainDb - list.music.duckDb) : list.music.ducking ? dbToGain(list.music.gainDb - list.music.duckDb * 0.5) : dbToGain(list.music.gainDb);
      music.volume = ducked;
      if (active) {
        const lengthMs = Number.isFinite(music.duration) && music.duration > 0 ? music.duration * 1000 : null;
        const startIn = Math.min(list.music.inMs || 0, lengthMs ? Math.max(0, lengthMs - 100) : Infinity);
        const span = lengthMs ? Math.max(100, lengthMs - startIn) : null;
        const want = span && list.music.loop ? startIn + (local % span) : startIn + local;
        if (lengthMs && !list.music.loop && want >= lengthMs) {
          if (!music.paused) music.pause();
        } else {
          if (Math.abs(music.currentTime * 1000 - want) > DRIFT_MS) music.currentTime = want / 1000;
          if (music.paused) music.play().catch(() => undefined);
        }
      } else if (!music.paused) {
        music.pause();
      }
    }
  }, [list, muted, outputMs, playing, segmentMuted, video]);

  // Levels for the meters: route the elements through analysers once, then read peaks.
  useEffect(() => {
    if (!meter || !playing) {
      setLevels(SILENT);
      return undefined;
    }
    const AudioCtor: typeof AudioContext | undefined = typeof window !== 'undefined' ? (window.AudioContext || (window as any).webkitAudioContext) : undefined;
    if (!AudioCtor) return undefined;
    if (!contextRef.current) {
      try {
        contextRef.current = new AudioCtor();
      } catch {
        return undefined;
      }
    }
    const context = contextRef.current;
    if (context.state === 'suspended') context.resume().catch(() => undefined);
    const attach = (el: HTMLMediaElement | null): Analysed | null => {
      if (!el || el.crossOrigin !== 'anonymous') return null;
      const known = analysedRef.current.get(el);
      if (known) return known;
      try {
        const source = context.createMediaElementSource(el);
        const analyser = context.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        analyser.connect(context.destination);
        const entry = { source, analyser };
        analysedRef.current.set(el, entry);
        return entry;
      } catch {
        return null;
      }
    };
    const buffer = new Float32Array(512);
    const peak = (entry: Analysed | null): number => {
      if (!entry) return 0;
      entry.analyser.getFloatTimeDomainData(buffer);
      let max = 0;
      for (let i = 0; i < buffer.length; i += 1) max = Math.max(max, Math.abs(buffer[i]));
      return Math.min(1, max);
    };
    const timer = window.setInterval(() => {
      const original = peak(attach(video));
      const music = peak(attach(musicRef.current));
      const voice = Object.keys(voicesRef.current).reduce((max, id) => Math.max(max, peak(attach(voicesRef.current[id]))), 0);
      setLevels({ original, music, voice });
    }, 100);
    return () => window.clearInterval(timer);
  }, [meter, playing, video]);

  // Silence everything when the studio closes.
  useEffect(() => () => {
    if (musicRef.current) musicRef.current.pause();
    Object.keys(voicesRef.current).forEach((id) => voicesRef.current[id].pause());
    if (contextRef.current) contextRef.current.close().catch(() => undefined);
  }, []);

  return levels;
}

/** An <audio> for a brand-kit file; PithPlay's own links allow the app's origin, so it can be metered. */
function makeAudio(url: string): HTMLAudioElement {
  const el = new Audio();
  if (isReadableAssetUrl(url)) el.crossOrigin = 'anonymous';
  el.preload = 'auto';
  el.src = url;
  return el;
}

/** PithPlay's API asset links send CORS headers for the app, so their media can be read (meters, thumbnails). */
export const isReadableAssetUrl = (url: string | null | undefined): boolean => Boolean(url && /\/api\/assets\//.test(url));
